import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { HeaderLogo } from "../brand/HeaderLogo";
import { Logo } from "../brand/Logo";
import { AnnouncementBar } from "./AnnouncementBar";
import { MagnifyingGlass, ShoppingBag, User, SignOut, List, X } from "@phosphor-icons/react";
import { useAuth } from "../../lib/auth";
import { useCart } from "../../lib/cart";
import { scrollAndFocus } from "../../lib/hash-scroller";

const NAV = [
  { to: "/shop",         label: "Shop",           testid: "nav-shop" },
  { to: "/quality",      label: "Quality",        testid: "nav-quality" },
  { to: "/coa-library",  label: "COA Library",    testid: "nav-coa-library" },
  { to: "/#how-it-works",label: "How It Works",   testid: "nav-how-it-works" },
  { to: "/faq",          label: "FAQ",            testid: "nav-faq" },
  { to: "/blog",         label: "Blog",           testid: "nav-blog" },
  { to: "/about",        label: "About",          testid: "nav-about" },
];

export const Header = () => {
  const { user, logout } = useAuth();
  const { count, setDrawerOpen } = useCart();
  const nav = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); setMobileSearchOpen(false); }, [location.pathname]);

  // Eligibility-gate routes are Google Ads landing pages: the "only" navigation
  // options must be the four allowed destinations. Suppress the primary NAV +
  // search + cart on these routes so we do not expose the shop catalog until
  // the visitor explicitly confirms research-use eligibility.
  const isEligibilityRoute =
    location.pathname === "/research-eligibility" ||
    location.pathname === "/research-eligibility/declined";

  if (isEligibilityRoute) {
    // Google Ads landing page: strip everything except the brand banner.
    // The compliance announcement bar is intentionally suppressed here —
    // the on-page fine-print block below the CTAs carries the same
    // Research-Use disclosures and the Research-Use Policy link.
    //
    // Banner treatment: wide rectangular Medical Peptide Store wordmark
    // (the SAME asset used on the homepage hero and footer) inside a
    // full-width deep-navy band. Scales cleanly at 176 px width on
    // mobile → 240 px on desktop. This is the premium single-screen
    // landing surface — no navigation, no cart, no search.
    return (
      <header data-testid="site-header-minimal">
        <div className="w-full border-b border-[#315986] bg-[linear-gradient(120deg,#0d2341_0%,#15365e_52%,#0b203d_100%)]">
          <div className="mps-container flex items-center justify-center py-7 sm:py-8 md:py-8">
            <Logo
              widthClass="w-[min(82vw,360px)] sm:w-[420px] md:w-[480px]"
              className="drop-shadow-[0_18px_44px_rgba(0,0,0,0.28)]"
            />
          </div>
        </div>
      </header>
    );
  }

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setMobileSearchOpen(false);
    nav(`/shop?search=${encodeURIComponent(q.trim())}`);
  };

  /**
   * Hash-link click handler.  When we're already on the target route,
   * react-router v6 collapses the same-URL push into a replace and
   * `useLocation` does not always re-fire — which is why the naked
   * `<Link to="/#how-it-works">` used to leave `scrollY` at 0.
   * Intercepting the click here forces a deterministic scroll for
   * every same-route case while letting cross-route clicks fall
   * through to the Link's normal navigation (HashScroller then scrolls
   * once Home mounts).
   */
  const handleHashLinkClick = (to) => (e) => {
    const [path, rawHash] = to.split("#");
    if (!rawHash) return;                    // not a hash link
    const targetPath = path || "/";
    if (location.pathname !== targetPath) return; // let Link navigate
    // Same-route click: intercept.
    e.preventDefault();
    const el = document.getElementById(decodeURIComponent(rawHash));
    if (el) {
      scrollAndFocus(el);
      // Sync the URL bar so back/forward + copy-link both keep working
      // — replaceState avoids adding a duplicate history entry.
      if (typeof window !== "undefined" && window.history && window.history.replaceState) {
        try {
          window.history.replaceState(null, "", `${targetPath}#${rawHash}`);
        } catch (_e) { /* noop */ }
      }
    }
  };

  return (
    <header className="sticky top-0 z-40" data-testid="site-header">
      <AnnouncementBar />
      <div className="mps-header-blur">
        <div className="mps-container flex items-center gap-6 h-24 md:h-28">
          <HeaderLogo />
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8 ml-2 flex-1" data-testid="primary-nav">
            {NAV.map((n) => {
              const active = location.pathname === n.to || location.pathname.startsWith(n.to + "/");
              const isHash = n.to.includes("#");
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={n.testid}
                  onClick={isHash ? handleHashLinkClick(n.to) : undefined}
                  className={`text-[14px] font-medium tracking-tight whitespace-nowrap transition-colors duration-200 ${
                    active ? "text-brand-navy" : "text-brand-muted hover:text-brand-navy"
                  }`}
                >
                  {n.label}
                </Link>
              );
            })}
          </nav>

          <form onSubmit={submitSearch} className="hidden md:flex items-center relative w-52 xl:w-64" data-testid="header-search-form">
            <MagnifyingGlass size={16} className="absolute left-4 text-brand-muted" weight="regular" aria-hidden />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search 100+ peptides…"
              aria-label="Search research peptides"
              data-testid="header-search-input"
              className="w-full bg-brand-surface border border-transparent rounded-full pl-11 pr-4 py-2.5 text-sm text-brand-text placeholder:text-brand-muted focus:bg-white focus:border-brand-accent focus:outline-none transition-colors duration-200"
            />
          </form>

          <div className="flex items-center gap-2 md:gap-3 ml-auto">
            {/* Mobile-only search icon — expands an inline search row.  Hides
                once the md breakpoint reveals the inline input above. */}
            <button
              type="button"
              onClick={() => setMobileSearchOpen((v) => !v)}
              data-testid="header-mobile-search-toggle"
              aria-label="Search"
              aria-expanded={mobileSearchOpen}
              className="md:hidden inline-flex items-center justify-center h-11 w-11 rounded-full text-brand-navy hover:bg-brand-surface transition-colors"
            >
              <MagnifyingGlass size={20} weight="regular" />
            </button>

            {user ? (
              <div className="hidden md:flex items-center gap-3 pl-3 border-l border-brand-border">
                <Link
                  to={user.role === "customer" ? "/account" : "/admin/products"}
                  data-testid="header-account-link"
                  className="inline-flex items-center gap-2 text-sm text-brand-text hover:text-brand-navy transition-colors"
                >
                  <User size={16} weight="regular" className="text-brand-muted" />
                  <span className="max-w-[120px] truncate font-medium">{user.full_name}</span>
                </Link>
                <button
                  onClick={() => { logout(); nav("/"); }}
                  data-testid="header-logout-btn"
                  className="text-brand-muted hover:text-brand-navy transition-colors p-1"
                  aria-label="Sign out"
                >
                  <SignOut size={16} weight="regular" />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                data-testid="header-login-link"
                className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-brand-text hover:text-brand-navy transition-colors"
              >
                <User size={16} weight="regular" className="text-brand-muted" />
                Login
              </Link>
            )}

            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              data-testid="header-cart-link"
              className="relative inline-flex items-center gap-1.5 h-11 min-w-11 px-2.5 md:px-3 rounded-full text-brand-navy hover:bg-brand-surface transition-colors duration-200"
              aria-label={count > 0 ? `Open cart · ${count} item${count === 1 ? "" : "s"}` : "Open cart"}
            >
              <ShoppingBag size={20} weight="regular" />
              <span className="hidden md:inline text-[13px] font-semibold">Cart</span>
              {count > 0 && (
                <span
                  data-testid="header-cart-count"
                  className="ml-0.5 inline-flex items-center justify-center bg-brand-accent text-white text-[10px] font-bold min-w-[20px] h-[20px] px-1.5 rounded-full"
                >
                  {count}
                </span>
              )}
            </button>

            <button
              className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full text-brand-navy hover:bg-brand-surface transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="header-mobile-toggle"
              aria-label="Menu"
            >
              {mobileOpen ? <X size={20} weight="regular" /> : <List size={20} weight="regular" />}
            </button>
          </div>
        </div>

        {mobileSearchOpen && (
          <div className="md:hidden border-t border-brand-border bg-white px-4 py-3" data-testid="mobile-search-row">
            <form onSubmit={submitSearch} className="relative">
              <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" weight="regular" aria-hidden />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search 100+ peptides…"
                aria-label="Search research peptides"
                data-testid="mobile-search-input"
                className="w-full bg-brand-surface border border-brand-border rounded-full pl-11 pr-4 py-3 text-sm text-brand-text placeholder:text-brand-muted focus:bg-white focus:border-brand-accent focus:outline-none transition-colors duration-200"
              />
            </form>
          </div>
        )}

        {mobileOpen && (
          <div className="lg:hidden border-t border-brand-border bg-white px-6 py-4 flex flex-col gap-1" data-testid="mobile-nav">
            {NAV.map((n) => {
              const isHash = n.to.includes("#");
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`${n.testid}-mobile`}
                  onClick={(e) => {
                    if (isHash) handleHashLinkClick(n.to)(e);
                    setMobileOpen(false);
                  }}
                  className="px-3 py-3 text-base font-medium text-brand-text hover:text-brand-navy hover:bg-brand-surface rounded-lg transition-colors"
                >
                  {n.label}
                </Link>
              );
            })}
            <div className="mt-2 pt-3 border-t border-brand-border">
              {user ? (
                <>
                  <Link
                    to={user.role === "customer" ? "/account" : "/admin/products"}
                    onClick={() => setMobileOpen(false)}
                    className="px-3 py-3 text-base font-medium text-brand-text block"
                  >
                    <User size={16} className="inline mr-2" weight="regular" />
                    {user.full_name}
                  </Link>
                  <button
                    onClick={() => { logout(); nav("/"); setMobileOpen(false); }}
                    className="px-3 py-3 text-base font-medium text-red-600 block w-full text-left"
                  >
                    <SignOut size={16} className="inline mr-2" weight="regular" />
                    Sign out
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-3 text-base font-medium text-brand-navy block"
                >
                  <User size={16} className="inline mr-2" weight="regular" />
                  Login
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
